import React, { useState, useMemo, useCallback } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, AwardBadge, Btn, Input, Badge, Sel, PilulaAwardTime, Modal } from "./ui";
import { supabase, isSupabaseReady } from "./supabase";
import { Icon } from "./Icons";
import { AWARDS } from "./data";
import { calcPontosCorretor, calcAwardVitalicio } from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// REDE DE RELACIONAMENTO COMERCIAL
//
// Árvore visual de indicações (até 10 níveis), links de convite,
// ativações, cadastro direto de novos corretores na rede.
// ═══════════════════════════════════════════════════════════════════════

// Helper: retorna texto baseado no sexo do corretor
function gen(c, masc, fem) { return c?.sexo === "F" ? fem : masc; }

export default function RedeRelacionamento({ corretores, setCorretores, times, currentUser }) {
  const [vista, setVista] = useState("arvore"); // arvore | stats | cadastrar
  const [expandidos, setExpandidos] = useState({});
  const [buscaArvore, setBuscaArvore] = useState("");
  const [corretorFoco, setCorretorFoco] = useState(null);

  // ─── Escopo: "minha" (rede do corretor logado) | "bellanor" (toda rede)
  const isAdmin = currentUser.role === "admin" || currentUser.role === "admin_master" || currentUser.role === "proprietario" || currentUser.role === "comercial";
  const [escopo, setEscopo] = useState(isAdmin ? "bellanor" : "minha");

  // ─── Modal reindicar (admin) ───────────────────────────────────────
  const [reindicarTarget, setReindicarTarget] = useState(null);
  const [reindicarNovoId, setReindicarNovoId] = useState("");
  const [reindicarSalvando, setReindicarSalvando] = useState(false);

  async function salvarReindicacao() {
    if (!reindicarTarget || !isSupabaseReady) return;
    setReindicarSalvando(true);
    try {
      const targetUuid = reindicarTarget._uuid || reindicarTarget.id;
      const novoIndicadorUuid = reindicarNovoId === "__none__" ? null
        : corretores.find(c => (c._uuid === reindicarNovoId || String(c.id) === reindicarNovoId))?._uuid || reindicarNovoId;
      const { error } = await supabase.from("corretores")
        .update({ indicado_por: novoIndicadorUuid })
        .eq("id", targetUuid);
      if (error) throw error;
      // local update
      if (setCorretores) {
        setCorretores(prev => prev.map(c =>
          (c._uuid === targetUuid || c.id === reindicarTarget.id)
            ? { ...c, indicadoPor: novoIndicadorUuid, indicado_por: novoIndicadorUuid }
            : c
        ));
      }
      setReindicarTarget(null); setReindicarNovoId("");
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setReindicarSalvando(false);
    }
  }


  const { raizes, filhosDe, totalNiveis } = useMemo(() => {
    const mapa = {};
    // IDs visíveis na lista (pra detectar pais "fora" — ex: proprietário sem ficha de corretor)
    const visiveis = new Set();
    corretores.forEach(c => {
      if (c.id != null) visiveis.add(c.id);
      if (c._uuid)      visiveis.add(c._uuid);
    });
    corretores.forEach(c => {
      let pid = c.indicadoPor || c.indicado_por || null;
      // Pai inexistente na lista → trata como raiz (ex: indicado por proprietário sem ficha)
      if (pid && !visiveis.has(pid)) pid = null;
      if (!mapa[pid]) mapa[pid] = [];
      mapa[pid].push(c);
    });

    let raizes;
    if (escopo === "bellanor") {
      raizes = mapa[null] || [];
    } else {
      const meuCorretor = corretores.find(c =>
        c._uuid === currentUser._corretorUuid || c.id === currentUser.corretorId
      );
      raizes = meuCorretor ? [meuCorretor] : [];
    }

    function depth(id, level) {
      const filhos = mapa[id] || [];
      if (filhos.length === 0) return level;
      return Math.max(...filhos.map(f => depth(f.id, level + 1)));
    }
    const totalNiveis = Math.max(1, ...raizes.map(r => depth(r.id, 1)));

    return { raizes, filhosDe: mapa, totalNiveis };
  }, [corretores, escopo, currentUser]);

  // ─── Stats por nível ───────────────────────────────────────────────
  const statsPorNivel = useMemo(() => {
    const niveis = [];
    function traverse(lista, nivel) {
      if (!niveis[nivel]) niveis[nivel] = { nivel: nivel + 1, total: 0, ativos: 0, inativos: 0, volume: 0 };
      lista.forEach(c => {
        niveis[nivel].total++;
        if (c.ativo !== false) niveis[nivel].ativos++;
        else niveis[nivel].inativos++;
        niveis[nivel].volume += c.vendasValor || 0;
        const filhos = filhosDe[c.id] || [];
        if (filhos.length > 0) traverse(filhos, nivel + 1);
      });
    }
    traverse(raizes, 0);
    return niveis.filter(Boolean);
  }, [raizes, filhosDe]);

  // ─── Toggle expandir/colapsar ──────────────────────────────────────
  const toggle = useCallback((id) => {
    setExpandidos(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const expandirTudo = () => {
    const todos = {};
    corretores.forEach(c => { todos[c.id] = true; });
    setExpandidos(todos);
  };

  const colapsarTudo = () => setExpandidos({});

  const tudoExpandido = Object.values(expandidos).filter(Boolean).length >= corretores.filter(c => (filhosDe[c.id] || []).length > 0).length;
  const toggleExpansao = () => tudoExpandido ? colapsarTudo() : expandirTudo();

  // ─── Copiar link de indicação ──────────────────────────────────────
  const copiarLink = (codigo) => {
    const link = `https://app.bellanor.com.br/altaperformance?ref=${codigo}`;
    navigator.clipboard.writeText(link).then(() => {
      alert(`Link copiado!\n${link}`);
    }).catch(() => {
      prompt("Copie o link:", link);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader
        icon="🌳"
        title="Rede de Relacionamento Comercial"
        subtitle={escopo === "minha" ? `Sua rede · ${totalNiveis} nível(eis) de profundidade` : `${corretores.length} corretoras · ${totalNiveis} nível(eis) de profundidade`}
      />

      {/* ═══ CORRETOR: 4 botões unificados ═══ */}
      {!isAdmin && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { id: "minha",     label: "🌳 Minha Rede" },
            { id: "geral",     label: "🌎 Geral" },
            { id: "stats",     label: "📊 Estatísticas" },
            { id: "cadastrar", label: "➕ Cadastrar" },
          ].map(s => {
            const ativo =
              (s.id === "minha"     && escopo === "minha"    && vista === "arvore") ||
              (s.id === "geral"     && escopo === "bellanor" && vista === "arvore") ||
              (s.id === "stats"     && vista === "stats") ||
              (s.id === "cadastrar" && vista === "cadastrar");
            return (
              <button key={s.id} onClick={() => {
                if (s.id === "minha")     { setEscopo("minha");    setVista("arvore"); }
                if (s.id === "geral")     { setEscopo("bellanor"); setVista("arvore"); }
                if (s.id === "stats")     { setVista("stats"); }
                if (s.id === "cadastrar") { setVista("cadastrar"); }
              }} style={{
                padding: "10px 16px", borderRadius: T.r.md,
                background: ativo ? T.brand + "22" : T.card,
                border: `1px solid ${ativo ? T.brand : T.border}`,
                color: ativo ? T.brand : T.textSec,
                cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
              }}>{s.label}</button>
            );
          })}
        </div>
      )}

      {/* ═══ TABS (admin) ═══ */}
      {isAdmin && (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[
          { id: "arvore", label: "🌳 Árvore", desc: "Visualizar rede" },
          { id: "stats", label: "📊 Estatísticas", desc: "Por nível" },
          { id: "cadastrar", label: "➕ Cadastrar", desc: "Novo corretor" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setVista(tab.id)} style={{
            padding: "10px 16px", borderRadius: T.r.md,
            background: vista === tab.id ? T.brand + "22" : T.card,
            border: `1px solid ${vista === tab.id ? T.brand : T.border}`,
            color: vista === tab.id ? T.brand : T.textSec,
            cursor: "pointer", fontSize: 12, fontWeight: 600,
            fontFamily: "inherit", transition: `all 160ms ${T.ease}`,
          }}>
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {/* ═══ ÁRVORE ═══ */}
      {vista === "arvore" && (
        <>
          {/* Toolbar */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Input
              placeholder="Buscar na rede..."
              value={buscaArvore}
              onChange={e => setBuscaArvore(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />
            <Btn variant="ghost" onClick={toggleExpansao} style={{ fontSize: 11 }}>
              {tudoExpandido ? "📦 Recolher tudo" : "📂 Expandir tudo"}
            </Btn>
          </div>

          {/* Árvore recursiva */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {raizes.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.textDim }}>
                Nenhuma corretora na rede ainda. Use a aba "Cadastrar" para adicionar.
              </div>
            ) : (
              raizes
                .filter(c => !buscaArvore || c.nome.toLowerCase().includes(buscaArvore.toLowerCase()))
                .map(c => (
                  <TreeNode
                    key={c.id}
                    corretor={c}
                    filhosDe={filhosDe}
                    expandidos={expandidos}
                    toggle={toggle}
                    times={times}
                    nivel={0}
                    copiarLink={copiarLink}
                    onFoco={setCorretorFoco}
                    busca={buscaArvore}
                    isAdmin={isAdmin}
                    onReindicar={(target) => { setReindicarTarget(target); setReindicarNovoId(""); }}
                  />
                ))
            )}
          </div>
        </>
      )}

      {/* ═══ ESTATÍSTICAS POR NÍVEL ═══ */}
      {vista === "stats" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Resumo geral */}
          <Card>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14 }}>
              <StatBox label="Total na rede" valor={corretores.length} cor={T.brand} />
              <StatBox label="Ativos" valor={corretores.filter(c => c.ativo !== false).length} cor={T.ok} />
              <StatBox label="Inativos (90d)" valor={corretores.filter(c => c.ativo === false).length} cor={T.err} />
              <StatBox label="Níveis ativos" valor={totalNiveis} cor={T.gold} />
              <StatBox label="Volume total" valor={fmt.brl(corretores.reduce((s, c) => s + (c.vendasValor || 0), 0))} cor={T.info} isText />
            </div>
          </Card>

          {/* Por nível */}
          <Card>
            <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
              📊 Detalhamento por Nível
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {statsPorNivel.map(n => {
                const pct = n.total > 0 ? Math.round((n.ativos / n.total) * 100) : 0;
                return (
                  <div key={n.nivel} style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px",
                    background: T.cardSoft, border: `1px solid ${T.border}`,
                    borderRadius: T.r.md,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: T.r.md,
                      background: T.brand + "22", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: 14, fontWeight: 700, color: T.brand,
                    }}>
                      N{n.nivel}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                          {n.nivel === 1 ? "Vendas Diretas" : `Nível ${n.nivel} da Rede`}
                        </span>
                        {isAdmin && <Badge text={`${n.total} corretora${n.total !== 1 ? "s" : ""}`} tone="brand" />}
                        {isAdmin && n.inativos > 0 && <Badge text={`${n.inativos} inativa${n.inativos !== 1 ? "s" : ""}`} tone="err" />}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                        <span style={{ fontSize: 10, color: T.textSub }}>
                          {pct}% ativas · {fmt.brl(n.volume)} volume
                        </span>
                      </div>
                    </div>
                    {/* Mini barra */}
                    <div style={{ width: 60, height: 4, background: T.elev, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: T.ok, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
              {statsPorNivel.length === 0 && (
                <div style={{ textAlign: "center", padding: 20, color: T.textDim, fontSize: 13 }}>
                  Nenhum dado de rede disponível ainda.
                </div>
              )}
            </div>
          </Card>

          {/* Awards na rede */}
          <Card>
            <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
              🎖️ Distribuição de Awards na Rede
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {AWARDS.map(a => {
                const count = corretores.filter(c => {
                  const aw = calcAwardVitalicio(c);
                  return aw.id === a.id;
                }).length;
                const ac = AWARD_COLORS[a.id] || {};
                return (
                  <div key={a.id} style={{
                    flex: "1 1 70px", minWidth: 70, textAlign: "center",
                    padding: "10px 8px", borderRadius: T.r.md,
                    background: ac.bg || T.cardSoft,
                    border: `1px solid ${ac.border || T.border}`,
                  }}>
                    <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: ac.fg || T.text }}>{count}</div>
                    <div style={{ fontSize: 8, color: T.textSub, textTransform: "uppercase", marginTop: 2 }}>{a.nome.replace(" Award", "")}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* ═══ CADASTRAR NOVA CORRETORA ═══ */}
      {vista === "cadastrar" && (
        <CadastroRede corretores={corretores} setCorretores={setCorretores} times={times} />
      )}

      {/* ═══ DETALHE DO CORRETOR (modal lateral) ═══ */}
      {corretorFoco && (
        <DetalheCorretor
          corretor={corretorFoco}
          corretores={corretores}
          times={times}
          filhosDe={filhosDe}
          onClose={() => setCorretorFoco(null)}
          copiarLink={copiarLink}
        />
      )}

      {/* ═══ MODAL REINDICAR (admin) ═══ */}
      {reindicarTarget && (
        <Modal title={`Mudar indicador de ${reindicarTarget.nome}`} onClose={() => !reindicarSalvando && setReindicarTarget(null)} width={520}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: T.textSub }}>
              Indicador atual: <strong style={{ color: T.text }}>{
                (() => {
                  const indId = reindicarTarget.indicadoPor || reindicarTarget.indicado_por;
                  if (!indId) return "— (raiz)";
                  const ind = corretores.find(c => c._uuid === indId || c.id === indId);
                  return ind?.nome || "?";
                })()
              }</strong>
            </div>
            <Sel label="Novo indicador" value={reindicarNovoId} onChange={e => setReindicarNovoId(e.target.value)}>
              <option value="">— selecione —</option>
              <option value="__none__">⭐ Sem indicador (vira raiz)</option>
              {corretores
                .filter(c => c.id !== reindicarTarget.id && c._uuid !== reindicarTarget._uuid)
                .sort((a, b) => a.nome.localeCompare(b.nome))
                .map(c => (
                  <option key={c.id} value={c._uuid || c.id}>{c.nome} · {c.cidade}-{c.uf}</option>
                ))
              }
            </Sel>
            <div style={{ padding: "8px 12px", borderRadius: T.r.md, background: T.warn + "15", border: `1px solid ${T.warn}44`, color: T.warn, fontSize: 11 }}>
              ⚠️ Atenção: ao reindicar, toda a subárvore deste corretor muda de posição. Verifique impacto nos cálculos de comissão.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setReindicarTarget(null)} disabled={reindicarSalvando}>Cancelar</Btn>
              <Btn onClick={salvarReindicacao} disabled={reindicarSalvando || !reindicarNovoId}>{reindicarSalvando ? "Salvando..." : "💾 Salvar"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TREE NODE — nó recursivo da árvore
// ═══════════════════════════════════════════════════════════════════════

function TreeNode({ corretor, filhosDe, expandidos, toggle, times, nivel, copiarLink, onFoco, busca, isAdmin, onReindicar }) {
  const c = corretor;
  const filhos = filhosDe[c.id] || [];
  const temFilhos = filhos.length > 0;
  const expandido = expandidos[c.id] || false;
  const aw = calcAwardVitalicio(c);
  const ac = AWARD_COLORS[aw.id] || {};
  const timeDoc = c.timeId && times ? times.find(t => t.id === c.timeId) : null;
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const inativo = c.ativo === false;

  // Cores por nível (alternadas)
  const nivelCores = [T.brand, T.gold, T.info, T.ok, T.platinum, T.err];
  const corNivel = nivelCores[nivel % nivelCores.length];

  // Filtra filhos na busca
  const filhosVisiveis = busca
    ? filhos.filter(f => f.nome.toLowerCase().includes(busca.toLowerCase()))
    : filhos;

  return (
    <div style={{ marginLeft: nivel > 0 ? 24 : 0 }}>
      {/* Linha do galho */}
      {nivel > 0 && (
        <div style={{
          position: "relative", marginLeft: -12,
          borderLeft: `2px solid ${corNivel}44`,
          height: 12,
        }} />
      )}

      {/* Card do nó */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px",
        background: inativo ? T.elev : T.card,
        border: `1px solid ${inativo ? T.border : corNivel + "44"}`,
        borderLeft: `3px solid ${corNivel}`,
        borderRadius: T.r.md,
        opacity: inativo ? 0.6 : 1,
        transition: `all 160ms ${T.ease}`,
        cursor: "pointer",
      }}
        onClick={() => onFoco(c)}
        onMouseEnter={e => { e.currentTarget.style.background = T.cardHi; }}
        onMouseLeave={e => { e.currentTarget.style.background = inativo ? T.elev : T.card; }}
      >
        {/* Expandir/colapsar */}
        {temFilhos ? (
          <button
            onClick={e => { e.stopPropagation(); toggle(c.id); }}
            style={{
              width: 22, height: 22, borderRadius: 4,
              background: corNivel + "22", border: `1px solid ${corNivel}44`,
              color: corNivel, fontSize: 12, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "inherit", fontWeight: 700,
              transform: expandido ? "rotate(90deg)" : "none",
              transition: `transform 160ms ${T.ease}`,
            }}
          >
            ▶
          </button>
        ) : (
          <div style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: corNivel + "44" }} />
          </div>
        )}

        {/* Avatar */}
        <Avatar initials={initials} size={32} photo={c.foto} />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{c.nome}</span>
            <PilulaAwardTime awardId={aw.id} timeEmoji={timeDoc?.emoji} size="xs" founder={c.tipo === "proprietario"} />
            {inativo && <Badge text="INATIVA 90d" tone="err" />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: T.textSub }}>📍 {c.cidade}-{c.uf}</span>
            <span style={{ fontSize: 10, color: T.gold }}>{c.vendasDiretas || 0} vendas · {fmt.brl(c.vendasValor || 0)}</span>
            {temFilhos && (
              <span style={{ fontSize: 10, color: corNivel, fontWeight: 600 }}>
                🌿 {filhos.length} {gen(filhos[0], "indicado", "indicada")}{filhos.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Link de indicação */}
        {(c.tagUnica || c.tag_unica) && (
          <button
            onClick={e => { e.stopPropagation(); copiarLink(c.tagUnica || c.tag_unica); }}
            title={`Copiar link de indicação`}
            style={{
              padding: "4px 8px", borderRadius: 4,
              background: T.brand + "15", border: `1px solid ${T.brand}33`,
              color: T.brand, fontSize: 10, cursor: "pointer",
              fontFamily: "inherit", fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            🔗 {c.tagUnica || c.tag_unica}
          </button>
        )}

        {/* Nível badge */}
        <div style={{
          padding: "2px 8px", borderRadius: 100,
          background: corNivel + "15", border: `1px solid ${corNivel}33`,
          fontSize: 9, fontWeight: 700, color: corNivel,
          whiteSpace: "nowrap",
        }}>
          N{nivel + 1}
        </div>

        {/* Admin: mudar indicador */}
        {isAdmin && onReindicar && (
          <button
            onClick={e => { e.stopPropagation(); onReindicar(c); }}
            title="Mudar indicador"
            style={{
              padding: "4px 8px", borderRadius: 4,
              background: T.warn + "15", border: `1px solid ${T.warn}33`,
              color: T.warn, fontSize: 10, cursor: "pointer",
              fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap",
            }}
          >🔧 Reindicar</button>
        )}
      </div>

      {/* Filhos expandidos */}
      {temFilhos && expandido && (
        <div style={{
          borderLeft: `2px dashed ${corNivel}33`,
          marginLeft: 11,
          paddingLeft: 0,
          marginTop: 2,
          display: "flex", flexDirection: "column", gap: 2,
        }}>
          {filhosVisiveis.map(f => (
            <TreeNode
              key={f.id}
              corretor={f}
              filhosDe={filhosDe}
              expandidos={expandidos}
              toggle={toggle}
              times={times}
              nivel={nivel + 1}
              copiarLink={copiarLink}
              onFoco={onFoco}
              busca={busca}
              isAdmin={isAdmin}
              onReindicar={onReindicar}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DETALHE DO CORRETOR — painel lateral
// ═══════════════════════════════════════════════════════════════════════

function DetalheCorretor({ corretor, corretores, times, filhosDe, onClose, copiarLink }) {
  const c = corretor;
  const aw = calcAwardVitalicio(c);
  const ac = AWARD_COLORS[aw.id] || {};
  const timeDoc = c.timeId && times ? times.find(t => t.id === c.timeId) : null;
  const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const filhos = filhosDe[c.id] || [];
  const indicadoPorNome = c.indicadoPor ? corretores.find(x => x.id === c.indicadoPor)?.nome : null;
  const p = calcPontosCorretor(c);

  // Contagem recursiva da rede abaixo
  function contarRede(id) {
    const filhos = filhosDe[id] || [];
    return filhos.reduce((s, f) => s + 1 + contarRede(f.id), 0);
  }
  const totalRede = contarRede(c.id);

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0,
      width: "min(400px, 90vw)", zIndex: 999,
      background: T.bg, borderLeft: `1px solid ${T.border}`,
      boxShadow: "-8px 0 32px rgba(0,0,0,0.4)",
      overflowY: "auto", padding: 20,
      display: "flex", flexDirection: "column", gap: 16,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
          Perfil na Rede
        </span>
        <button onClick={onClose} style={{
          width: 28, height: 28, borderRadius: "50%",
          background: T.elev, border: `1px solid ${T.border}`,
          cursor: "pointer", color: T.textDim, fontSize: 14,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>✕</button>
      </div>

      {/* Card principal */}
      <div style={{
        textAlign: "center", padding: 20,
        background: ac.bg || T.cardSoft,
        border: `1px solid ${ac.border || T.border}`,
        borderRadius: T.r.lg,
      }}>
        <Avatar initials={initials} size={56} photo={c.foto} />
        <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.text, marginTop: 10 }}>{c.nome}</div>
        <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
          📍 {c.cidade}-{c.uf} · {timeDoc ? `${timeDoc.emoji} ${timeDoc.nome}` : "Sem time"}
        </div>
        <div style={{ marginTop: 8 }}>
          <PilulaAwardTime awardId={aw.id} timeEmoji={timeDoc?.emoji} />
        </div>
      </div>

      {/* Link de indicação */}
      {c.codigoIndicacao && (
        <div style={{
          padding: "12px 14px", borderRadius: T.r.md,
          background: T.brand + "10", border: `1px solid ${T.brand}33`,
        }}>
          <div style={{ fontSize: 10, color: T.textSub, marginBottom: 6, fontWeight: 600 }}>🔗 LINK DE INDICAÇÃO</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a
              href={`https://app.bellanor.com.br/altaperformance?ref=${c.tagUnica || c.tag_unica || c.codigoIndicacao}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1, fontSize: 11, color: "#87CEEB", background: T.bg,
                padding: "6px 10px", borderRadius: 4, border: `1px solid #87CEEB44`,
                overflow: "hidden", textOverflow: "ellipsis",
                textDecoration: "underline", fontFamily: "monospace",
              }}
              title="Abrir link em nova aba"
            >
              app.bellanor.com.br/altaperformance?ref={c.tagUnica || c.tag_unica || c.codigoIndicacao}
            </a>
            <Btn variant="ghost" onClick={() => copiarLink(c.tagUnica || c.tag_unica || c.codigoIndicacao)} style={{ fontSize: 11, whiteSpace: "nowrap" }}>
              📋 Copiar
            </Btn>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MiniCard label="Vendas diretas" valor={c.vendasDiretas || 0} />
        <MiniCard label="Volume total" valor={fmt.brl(c.vendasValor || 0)} isText />
        <MiniCard label="Pontos" valor={p.total} />
        <MiniCard label="Rede abaixo" valor={totalRede} />
        <MiniCard label="Indicou direto" valor={filhos.length} />
        <MiniCard label="Ativações vivas" valor={(c.ativados || 0) - (c.ativadosInativos90d || 0)} />
      </div>

      {/* Quem indicou */}
      {indicadoPorNome && (
        <div style={{ fontSize: 11, color: T.textSub, padding: "8px 12px", background: T.cardSoft, borderRadius: T.r.md }}>
          👆 {gen(c, "Indicado", "Indicada")} por: <strong style={{ color: T.text }}>{indicadoPorNome}</strong>
        </div>
      )}

      {/* Indicados diretos */}
      {filhos.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 8 }}>
            👇 Indicou diretamente ({filhos.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {filhos.map(f => {
              const faw = calcAwardVitalicio(f);
              return (
                <div key={f.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", background: T.cardSoft,
                  borderRadius: T.r.sm, border: `1px solid ${T.border}`,
                  fontSize: 12, color: T.text,
                }}>
                  <span>{f.nome}</span>
                  <PilulaAwardTime awardId={faw.id} size="xs" />
                  <span style={{ marginLeft: "auto", fontSize: 10, color: T.textDim }}>
                    {f.vendasDiretas || 0} vendas
                  </span>
                  <Badge text={f.ativo !== false ? "ativa" : "inativa"} tone={f.ativo !== false ? "ok" : "err"} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CADASTRO NA REDE — adicionar nova corretora
// ═══════════════════════════════════════════════════════════════════════

function CadastroRede({ corretores, setCorretores, times }) {
  const [form, setForm] = useState({
    nome: "", email: "", telefone: "", cidade: "", uf: "",
    sexo: "M", timeId: "", indicadoPor: "",
  });
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const ufs = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

  async function salvar() {
    if (!form.nome.trim()) { setMsg({ tipo: "err", texto: "Nome é obrigatório." }); return; }
    if (!form.cidade.trim()) { setMsg({ tipo: "err", texto: "Cidade é obrigatória." }); return; }
    if (!form.uf) { setMsg({ tipo: "err", texto: "Selecione o estado." }); return; }

    const primeiroNome = form.nome.trim().split(" ")[0].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const codigo = primeiroNome + Math.floor(Math.random() * 900 + 100);

    // Resolve indicadoPor: pode ser numeric ID ou UUID
    let indicadoPorUuid = null;
    if (form.indicadoPor) {
      const ref = corretores.find(c => c.id === Number(form.indicadoPor));
      indicadoPorUuid = ref?._uuid || null;
    }

    // Resolve time UUID
    let timeUuid = null;
    if (form.timeId) {
      const t = times?.find(t => t.id === form.timeId);
      timeUuid = t?._uuid || null;
    }

    setSalvando(true);
    try {
      const { supabase, isSupabaseReady } = await import("./supabase");
      if (isSupabaseReady) {
        const { data, error } = await supabase
          .from("corretores")
          .insert({
            nome: form.nome.trim(),
            email: form.email.trim() || null,
            telefone: form.telefone.trim() || null,
            cidade: form.cidade.trim(),
            uf: form.uf,
            sexo: form.sexo,
            time_id: timeUuid,
            indicado_por: indicadoPorUuid,
            codigo_indicacao: codigo,
            tipo: "corretor",
            ativo: true,
          })
          .select()
          .single();

        if (error) throw error;

        // Adiciona ao state local com mapeamento
        const novoLocal = {
          id: Math.max(0, ...corretores.map(c => c.id)) + 1,
          _uuid: data.id,
          nome: data.nome, email: data.email, telefone: data.telefone,
          cidade: data.cidade, uf: data.uf, sexo: data.sexo,
          timeId: form.timeId || null,
          indicadoPor: form.indicadoPor ? Number(form.indicadoPor) : null,
          codigoIndicacao: data.codigo_indicacao,
          ativo: true, foto: null,
          vendasDiretas: 0, vendasValor: 0, vendasValorAno: 0, vendasValorMes: 0, vendasValorSemana: 0,
          pontosAno: 0, pontosMes: 0, pontosSemana: 0, ativacoesSemana: 0,
          cadastrados: 0, ativados: 0, ativadosInativos90d: 0,
          semanasComo1Time: 0, semanasComo1Regional: 0, semanasComo1Nacional: 0,
        };
        setCorretores(prev => [...prev, novoLocal]);
        setMsg({ tipo: "ok", texto: `✅ ${form.nome} ${form.sexo === "F" ? "cadastrada" : "cadastrado"}! Código: ${codigo}` });
      } else {
        throw new Error("Supabase não conectado");
      }
    } catch (err) {
      setMsg({ tipo: "err", texto: "Erro ao salvar: " + err.message });
    } finally {
      setSalvando(false);
    }
    setForm({ nome: "", email: "", telefone: "", cidade: "", uf: "", sexo: "M", timeId: "", indicadoPor: "" });
  }

  return (
    <Card>
      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 16 }}>
        ➕ Cadastrar Novo(a) Corretor(a) na Rede
      </div>

      {msg && (
        <div style={{
          padding: "10px 14px", borderRadius: T.r.md, marginBottom: 12,
          background: msg.tipo === "ok" ? T.okSoft : T.errSoft,
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}55`,
          color: msg.tipo === "ok" ? T.ok : T.err,
          fontSize: 12,
        }}>
          {msg.texto}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <Input label="Nome completo *" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Maria Silva" />
        </div>
        <Input label="E-mail" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="maria@email.com" />
        <Input label="Telefone" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} placeholder="(85) 99999-0000" />
        <Input label="Cidade *" value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} placeholder="Fortaleza" />
        <Sel label="Sexo" value={form.sexo} onChange={e => setForm({ ...form, sexo: e.target.value })}>
          <option value="M">Masculino</option>
          <option value="F">Feminino</option>
        </Sel>
        <Sel label="Estado *" value={form.uf} onChange={e => setForm({ ...form, uf: e.target.value })}>
          <option value="">Selecione</option>
          {ufs.map(u => <option key={u} value={u}>{u}</option>)}
        </Sel>
        <Sel label="Time" value={form.timeId} onChange={e => setForm({ ...form, timeId: e.target.value })}>
          <option value="">— Nenhum —</option>
          {(times || []).map(t => <option key={t.id} value={t.id}>{t.emoji} {t.nome}</option>)}
        </Sel>
        <Sel label="Indicado(a) por" value={form.indicadoPor} onChange={e => setForm({ ...form, indicadoPor: e.target.value })}>
          <option value="">— Nenhuma (raiz) —</option>
          {corretores.map(c => <option key={c.id} value={c.id}>{c.nome} · {c.cidade}-{c.uf}</option>)}
        </Sel>
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
        <Btn onClick={salvar} disabled={salvando} icon={<span>✅</span>}>{salvando ? "Salvando..." : "Cadastrar corretor(a)"}</Btn>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════

function StatBox({ label, valor, cor, isText }) {
  return (
    <div style={{
      padding: "14px 12px", borderRadius: T.r.md,
      background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${cor}`,
    }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div className="serif" style={{ fontSize: isText ? 16 : 22, fontWeight: 700, color: T.text }}>{valor}</div>
    </div>
  );
}

function MiniCard({ label, valor, isText }) {
  return (
    <div style={{
      padding: "10px 12px", borderRadius: T.r.md,
      background: T.cardSoft, border: `1px solid ${T.border}`,
    }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div className="serif" style={{ fontSize: isText ? 13 : 18, fontWeight: 600, color: T.text }}>{valor}</div>
    </div>
  );
}
